import { cva, type VariantProps } from 'class-variance-authority'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { cn } from '../../lib/cn.js'

const banner = cva(
  'flex items-start gap-3 rounded-md px-4 py-3 text-body [&_svg]:size-4 [&_svg]:mt-0.5 [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        info: 'bg-water-soft text-water-text',
        success: 'bg-teal-soft text-teal-text',
        attention: 'bg-amber-soft text-amber-ink',
        error: 'bg-error-soft text-error',
      },
    },
    defaultVariants: { tone: 'info' },
  },
)

const ICONS = {
  info: Info,
  success: CheckCircle2,
  attention: AlertTriangle,
  error: XCircle,
} as const

export interface StatusBannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>, VariantProps<typeof banner> {
  children: React.ReactNode
  /**
   * Lets the reader put it away.
   *
   * Only for a banner about something that **happened** — an invitation that
   * went onto a waiting list, a save that was refused. A banner about something
   * still true, like a paused listing, has no dismiss, because putting it away
   * would not make it untrue.
   */
  onDismiss?: () => void
}

/**
 * A sentence at the top of a form or a card, about the whole of it.
 *
 * Not a toast: a toast is for something that has finished and can be missed, and
 * this is for something still true — a card that was refused, a listing that is
 * not live yet, a message that says why nothing can be saved.
 *
 * An error one announces itself, because it usually appears in response to
 * something the person just pressed and they are not looking at the top of the page.
 */
export function StatusBanner({
  className,
  tone = 'info',
  children,
  onDismiss,
  ...props
}: StatusBannerProps) {
  const Icon = ICONS[tone ?? 'info']
  return (
    <div
      className={cn(banner({ tone }), className)}
      role={tone === 'error' ? 'alert' : 'status'}
      {...props}
    >
      <Icon aria-hidden />
      <span className="flex-1">{children}</span>
      {onDismiss && (
        <button
          type="button"
          aria-label="Dismiss"
          className="rounded-sm p-0.5 opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
          onClick={onDismiss}
        >
          <X aria-hidden />
        </button>
      )}
    </div>
  )
}
